import { FC, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Cancel,
  Delete as DeleteIcon,
  Save,
} from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import { useCreateObepHook } from "../../../hooks/useObepHook";
import { useInstituteContext } from "../../../context/institute.context";
import { useAuthUserState } from "../../../context/authUser.context";
import { logger } from "../../../utils/common";

// Mirrors OpenAPI `CreateObepRecordRequest` string constraints.
const NAME_MIN = 3;
const NAME_MAX = 100;
const DESCRIPTION_MIN = 10;
const DESCRIPTION_MAX = 500;
const MEASUREMENTS_MIN = 10;
const MEASUREMENTS_MAX = 500;
const OBJECTIVE_MIN = 3;
const OBJECTIVE_MAX = 100;

const obepSchema = z.object({
  name: z
    .string()
    .min(NAME_MIN, `Name must be at least ${NAME_MIN} characters`)
    .max(NAME_MAX, `Name must not exceed ${NAME_MAX} characters`),
  description: z
    .string()
    .min(DESCRIPTION_MIN, `Description must be at least ${DESCRIPTION_MIN} characters`)
    .max(
      DESCRIPTION_MAX,
      `Description must not exceed ${DESCRIPTION_MAX} characters`,
    ),
  measurements: z
    .string()
    .min(MEASUREMENTS_MIN, `Measurements must be at least ${MEASUREMENTS_MIN} characters`)
    .max(
      MEASUREMENTS_MAX,
      `Measurements must not exceed ${MEASUREMENTS_MAX} characters`,
    ),
  objectives: z
    .array(
      z.object({
        value: z
          .string()
          .refine(
            (s) => s.trim().length >= OBJECTIVE_MIN,
            `Objective must be at least ${OBJECTIVE_MIN} characters`,
          )
          .refine(
            (s) => s.trim().length <= OBJECTIVE_MAX,
            `Objective must not exceed ${OBJECTIVE_MAX} characters`,
          ),
      }),
    )
    .min(1, "At least one objective is required"),
});

type ObepFormData = z.infer<typeof obepSchema>;

interface ManageObepDialogProps {
  open: boolean;
  onClose: () => void;
}

const defaults: ObepFormData = {
  name: "",
  description: "",
  measurements: "",
  objectives: [],
};

const ManageObepDialog: FC<ManageObepDialogProps> = ({ open, onClose }) => {
  const { selectedInstitute } = useInstituteContext();
  const { userDetails } = useAuthUserState();
  const createObepMutation = useCreateObepHook();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ObepFormData>({
    resolver: zodResolver(obepSchema),
    defaultValues: defaults,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "objectives",
  });

  useEffect(() => {
    if (open) {
      reset(defaults);
    }
  }, [open, reset]);

  const onSubmit = async (data: ObepFormData) => {
    if (!selectedInstitute) {
      logger.show_error(
        "Please select an institute from the dashboard before creating an OBEP record.",
      );
      return;
    }
    if (!userDetails?.id) {
      logger.show_error(
        "Could not determine the logged-in user. Please sign in again.",
      );
      return;
    }

    const objectives = data.objectives.map((item) => item.value.trim());

    try {
      await createObepMutation.mutateAsync({
        name: data.name,
        description: data.description,
        measurements: data.measurements,
        objectives,
        instituteRef: selectedInstitute.id,
        createdBy: userDetails.id,
      });
      logger.show_success("OBEP record created successfully!");
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to create OBEP record. Please try again.",
      );
    }
  };

  const isLoading = isSubmitting || createObepMutation.isPending;
  const isError = createObepMutation.isError;
  const errorMessage = createObepMutation.error?.message;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-obep-dialog"
    >
      <DialogTitle data-testid="manage-obep-dialog-title">
        Add New OBEP Record
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="obep-form">
        <DialogContent data-testid="obep-form-content">
          {isError && (
            <Alert severity="error" sx={{ mb: 3 }} data-testid="obep-form-error">
              {errorMessage || "Failed to create OBEP record. Please try again."}
            </Alert>
          )}

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <ControlledTextField
              control={control}
              name="name"
              id="obep-name"
              label="Name"
              required
              errorMessage={errors.name?.message}
              inputProps={{ "data-testid": "obep-name-input" }}
            />
            <ControlledTextField
              control={control}
              name="measurements"
              id="obep-measurements"
              label="Measurements"
              required
              errorMessage={errors.measurements?.message}
              inputProps={{ "data-testid": "obep-measurements-input" }}
            />
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name="description"
                id="obep-description"
                label="Description"
                required
                multiline
                rows={3}
                errorMessage={errors.description?.message}
                inputProps={{ "data-testid": "obep-description-input" }}
                isGridItem={false}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography
                  variant="subtitle1"
                  component="h3"
                  data-testid="obep-objectives-title"
                >
                  Objectives
                </Typography>
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => append({ value: "" })}
                  data-testid="add-obep-objective-button"
                >
                  Add
                </Button>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Each objective must be {OBJECTIVE_MIN}–{OBJECTIVE_MAX}{" "}
                characters. All objectives must be filled in to save.
              </Typography>
              {typeof errors.objectives?.message === "string" && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ mb: 2 }}
                  data-testid="obep-objectives-error"
                >
                  {errors.objectives.message}
                </Typography>
              )}
              {fields.map((field, index) => (
                <Box
                  key={field.id}
                  display="flex"
                  alignItems="flex-start"
                  gap={1}
                  sx={{ mb: 2 }}
                  data-testid={`obep-objective-row-${index}`}
                >
                  <Box flex={1}>
                    <ControlledTextField
                      control={control}
                      name={`objectives.${index}.value`}
                      id={`obep-objective-${index}`}
                      label={`Objective ${index + 1}`}
                      required
                      multiline
                      rows={2}
                      errorMessage={errors.objectives?.[index]?.value?.message}
                      inputProps={{
                        "data-testid": `obep-objectives-input-${index}`,
                      }}
                      isGridItem={false}
                      fullWidth
                    />
                  </Box>
                  <IconButton
                    type="button"
                    onClick={() => remove(index)}
                    color="error"
                    size="small"
                    aria-label={`Remove objective ${index + 1}`}
                    data-testid={`remove-obep-objective-button-${index}`}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              {fields.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                  data-testid="obep-objectives-empty-hint"
                >
                  No objectives added. Click Add to create one.
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-obep-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-obep-button"
          >
            {isLoading ? "Creating..." : "Create OBEP Record"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageObepDialog;
